import { ApolloError } from "@apollo/client";
import {
  Box,
  Flex,
  StyleProps,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { HiOutlineInformationCircle } from "react-icons/hi";

import { Button, IconButton } from "../../../../components";
import CopyOutlineIcon from "../../../../components/Icons/CopyOutlineIcon";
import { useIsSmallScreen } from "../../../../hooks/useIsSmallScreen";
import { useSendGAEvent } from "../../../../utils/googleAnalytics";
import {
  CallNoteFragment,
  CallSpeakerFragment,
  CallSpeakerOption,
  TranscriptSegmentFragment,
} from "../../../graphql";
import { ClipRange } from "../../../types";
import { AskInputTrigger } from "../../Ask/AskInputTrigger";
import { MediaPlayerInterface } from "../../MediaPlayer/useMediaPlayer";
import TranscriptSearch from "./TranscriptSearch";
import TranscriptSegments from "./TranscriptSegments";
import useTranscriptSearch from "./useTranscriptSearch";

export type TranscriptProps = StyleProps & {
  callId?: string;
  transcript: TranscriptSegmentFragment[];
  autoScrollEnabled: boolean;
  speakers: Pick<
    CallSpeakerFragment,
    "label" | "speakerTag" | "isCandidate" | "identified"
  >[];
  player: Pick<
    MediaPlayerInterface,
    "playing" | "time" | "seek" | "play" | "duration"
  >;
  setAutoScrollEnabled(enabled: boolean): void;
  onCreateClipClick?(clipRange: ClipRange): void;
  onCopyTranscript?(): void;
  hideVideo?: () => void;
  focusVideo?: () => void;
  speakerOptions?: Pick<CallSpeakerOption, "id" | "label">[];
  canChangeSpeaker?: boolean;
  isVideoVisible?: boolean;
  isVideoCall?: boolean;
  notesToHighlight?: CallNoteFragment[];
  isSearchEnabled?: boolean;
  isCopyEnabled?: boolean;
  isWarningEnabled?: boolean;
  loading: boolean;
  error?: ApolloError;
  /** Callback to open the Ask Assistant panel */
  onAskOpen?(): void;
};

const Transcript: React.FC<TranscriptProps> = ({
  callId,
  transcript,
  autoScrollEnabled,
  speakers,
  player,
  notesToHighlight,
  setAutoScrollEnabled,
  onCreateClipClick,
  onCopyTranscript,
  hideVideo,
  focusVideo,
  speakerOptions,
  canChangeSpeaker = false,
  isVideoVisible = true,
  isVideoCall = true,
  isSearchEnabled = true,
  isCopyEnabled = true,
  isWarningEnabled = true,
  loading,
  error,
  onAskOpen,
  ...styles
}) => {
  const isSmallScreen = useIsSmallScreen();
  const { onToggle: toggleTranscriptWarning, isOpen: showTranscriptWarning } =
    useDisclosure();
  const sendGAEvent = useSendGAEvent();

  useEffect(() => {
    if (showTranscriptWarning) {
      window.addEventListener("click", toggleTranscriptWarning);
    }
    return () => {
      window.removeEventListener("click", toggleTranscriptWarning);
    };
  }, [showTranscriptWarning]);

  const {
    calculateSegmentPositions,
    searchTerm,
    setSearchTerm,
    matchPositions,
    segmentPositions,
    activeMatchIndex,
    activeMatchPosition,
    goToNextSearchResult,
    goToPrevSearchResult,
  } = useTranscriptSearch();

  useEffect(() => {
    calculateSegmentPositions(transcript);
  }, [transcript]);

  if (transcript.length === 0) {
    return (
      <Box px="8" py="4" textAlign="center" {...styles}>
        No transcript is available.
      </Box>
    );
  }

  const switchToVideoMode = (): void => {
    sendGAEvent("transcript_mode_toggle", "call_review", "video");
    focusVideo?.();
  };
  const switchToTranscriptMode = (): void => {
    sendGAEvent("transcript_mode_toggle", "call_review", "transcript");
    hideVideo?.();
  };

  const horizontalPadding = { base: 4, xl: 6 };

  return (
    <Flex direction="column" position="relative" h="100%" {...styles}>
      <Flex
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        position="relative"
        borderBottomColor="gray.100"
        borderBottomWidth="1px"
        pt={3}
        pb={3}
        px={horizontalPadding}
      >
        <Flex alignItems="center" flexGrow="1">
          <TranscriptSearch
            value={searchTerm}
            onChange={(value) => {
              if (player.playing) {
                setAutoScrollEnabled(false);
              }
              setSearchTerm(value);
            }}
            numMatches={matchPositions.length}
            activeMatchIndex={activeMatchIndex + 1}
            onPrevious={() => {
              goToPrevSearchResult();
              if (player.playing) {
                setAutoScrollEnabled(false);
              }
            }}
            onNext={() => {
              goToNextSearchResult();
              if (player.playing) {
                setAutoScrollEnabled(false);
              }
            }}
            onClear={() => {
              setSearchTerm("");
            }}
          />

          {!isSmallScreen && isWarningEnabled && (
            <IconButton
              aria-label="Show Transcript Quality Information"
              icon={<HiOutlineInformationCircle size="20px" />}
              color="blue.600"
              mr={{ lg: "0", xl: "2" }}
              bg="white"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                toggleTranscriptWarning();
              }}
            />
          )}

          {isCopyEnabled && onCopyTranscript && (
            <Tooltip label="Copy Transcript">
              <IconButton
                aria-label="Copy Transcript"
                icon={<CopyOutlineIcon width="18px" height="18px" />}
                color="blue.600"
                bg="white"
                variant="ghost"
                size="sm"
                onClick={onCopyTranscript}
                mr={{ lg: 2, xl: 3 }}
              />
            </Tooltip>
          )}
        </Flex>
        {isVideoCall && !isSmallScreen && (
          <Flex
            direction="row"
            alignItems="end"
            data-tour-id="ip2-video-transcript-toggle"
          >
            <Button
              borderTopRightRadius={0}
              borderBottomRightRadius={0}
              onClick={switchToVideoMode}
              disabled={isVideoVisible}
              variant="toggle"
              width="100px"
              aria-label="Show the recording"
            >
              Video
            </Button>
            <Button
              borderTopLeftRadius={0}
              borderBottomLeftRadius={0}
              onClick={switchToTranscriptMode}
              disabled={!isVideoVisible}
              variant="toggle"
              width="100px"
              aria-label="Hide the recording and focus on the transcript"
              px={{ lg: 3, xl: 4 }}
            >
              Transcript
            </Button>
          </Flex>
        )}
        {showTranscriptWarning && (
          <Box
            position="absolute"
            px={horizontalPadding}
            top="95%"
            left="0"
            zIndex="3"
            width="100%"
          >
            <Box
              bg="white"
              boxShadow="0px 8px 15px -3px rgba(0, 0, 0, 0.1), 0px -2px 6px -2px rgba(0, 0, 0, 0.05)"
              borderRadius="md"
              p="3"
            >
              <Box fontWeight="700" fontSize="sm" color="gray.700">
                This transcript was auto-generated using our speech-to-text
                technology.
              </Box>
              <Box
                as="span"
                fontWeight="400"
                fontSize="xs"
                color="gray.700"
                lineHeight="4"
              >
                Our engine is state of the art, but it&apos;s not perfect. Try
                using the search bar if you need to find specific keywords or
                phrases.
              </Box>
            </Box>
          </Box>
        )}
      </Flex>
      <TranscriptSegments
        callId={callId}
        transcript={transcript}
        autoScrollEnabled={autoScrollEnabled}
        speakers={speakers}
        player={player}
        notesToHighlight={notesToHighlight}
        setAutoScrollEnabled={setAutoScrollEnabled}
        onCreateClipClick={onCreateClipClick}
        speakerOptions={speakerOptions}
        canChangeSpeaker={canChangeSpeaker}
        isCopyEnabled={isCopyEnabled}
        calculateSegmentPositions={calculateSegmentPositions}
        matchPositions={matchPositions}
        segmentPositions={segmentPositions}
        activeMatchIndex={activeMatchIndex}
        activeMatchPosition={activeMatchPosition}
        horizontalPadding={horizontalPadding}
      />
      {onAskOpen && <AskInputTrigger onClick={onAskOpen} flexShrink={0} />}
    </Flex>
  );
};

export default Transcript;
